import * as React from "react"
import { useFuneralAnalytics } from "../../../provider"
import * as styles from "./style.module.css"

// Cookie-consent gate.
//
// WHY THIS EXISTS: the Dingwell site replaces dingwellfh.ca at the SAME domain,
// and the site being replaced runs a consent bar on every page. Shipping the
// replacement with GA4 enabled and no consent step is a compliance REGRESSION at
// a domain that previously asked — which is why this is a launch blocker, not a
// footer detail. See the parity plan's P0.6.
//
// HOW THE GATE WORKS: `gatsby-ssr.js` boots gtag with Google Consent Mode set to
// `analytics_storage: "denied"`, so no analytics cookie is written on first load.
// Accepting calls `gtag("consent", "update", …)` to flip it to "granted". The
// choice is persisted so the bar appears once.
//
// HYDRATION: renders null on the server AND on first client render, then reveals
// itself from an effect. Reading localStorage during render would produce a
// server/client mismatch.

export const CONSENT_KEY = "fui:cookie-consent"

const CookieConsent = ({
  // Defaults are the live Dingwell wording, verbatim.
  message = "This site uses cookies to improve your user experience. By continuing to use the site you're agreeing to our use of cookies.",
  // The second sentence exists ONLY to introduce the privacy-policy link, so it
  // is rendered only when there is a link to introduce — otherwise the banner
  // ends on a dangling "…click".
  linkPrefix = "To learn more about how we use your information, click",
  linkLabel = "here",
  // Omit to render the notice without a link — a missing privacy policy must not
  // stop the banner shipping.
  privacyUrl,
  acceptLabel = "AGREE & CONTINUE",
  // OFF BY DEFAULT, and deliberately so. Dingwell's banner reproduces the
  // wording and shape of the bar on the site it replaces, verbatim (DECISIONS
  // 2026-08-24) — adding a second button changes a live banner's appearance,
  // which is a compliance and product call for the owner to make, not a
  // side-effect of an analytics pass. The decline PATH is built and tested; flip
  // this to true per site to show it.
  showDecline = false,
  declineLabel = "DECLINE",
}) => {
  const { site } = useFuneralAnalytics()
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    try {
      if (!window.localStorage.getItem(CONSENT_KEY)) setVisible(true)
    } catch {
      // Private browsing / storage disabled: show the bar rather than silently
      // assuming consent.
      setVisible(true)
    }
  }, [])

  // `choice` is the analytics_storage value, so the persisted string, the gtag
  // call and the reported event can never disagree about what was chosen.
  const decide = choice => {
    try {
      window.localStorage.setItem(CONSENT_KEY, choice)
    } catch {
      // Non-fatal — the banner still dismisses for this page view.
    }
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", { analytics_storage: choice })
    }
    // Reported AFTER the consent update, so an acceptance is the first event
    // recorded with storage granted rather than the last one recorded without.
    site.consentChoice(choice)
    setVisible(false)
  }

  const accept = () => decide("granted")
  const decline = () => decide("denied")

  if (!visible) return null

  return (
    <div className={styles.banner} role="region" aria-label="Cookie notice">
      <div className={styles.inner}>
        <p className={styles.text}>
          {message}
          {privacyUrl ? (
            <>
              {" "}
              {linkPrefix}{" "}
              <a
                className={styles.link}
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {linkLabel}
              </a>
            </>
          ) : null}
        </p>
        <div className={styles.actions}>
          {showDecline && (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={decline}
            >
              {declineLabel}
            </button>
          )}
          <button type="button" className={styles.button} onClick={accept}>
            {acceptLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CookieConsent
